import {
  AsYouType,
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
  type CountryCode,
} from 'libphonenumber-js';

export const DEFAULT_PHONE_COUNTRY = 'BR';

function stripCountryCallingCode(country: string, digits: string): string {
  if (!digits || digits.startsWith('0800')) return digits;
  if (digits.startsWith('550800')) return digits.slice(2);

  if (country === 'BR') {
    let current = digits;
    while (
      current.startsWith('55') &&
      current.length >= 13 &&
      current.slice(2).startsWith('55') &&
      !current.slice(2).startsWith('559')
    ) {
      current = current.slice(2);
    }

    if (
      current.length === 11 &&
      current.startsWith('55') &&
      !current.startsWith('559')
    ) {
      return current.slice(2);
    }

    if (
      current.startsWith('55') &&
      (current.length === 12 || current.length === 13)
    ) {
      return current.slice(2);
    }

    return current;
  }

  try {
    const calling = String(getCountryCallingCode(country as CountryCode));
    if (!digits.startsWith(calling) || digits.length <= calling.length) {
      return digits;
    }

    const rest = digits.slice(calling.length);
    if (rest.length < 8) return digits;
    return rest;
  } catch {
    return digits;
  }
}

export interface IPhoneCountry {
  iso: string;
  name: string;
  callingCode: string;
  title: string;
  value: string;
  searchText: string;
}

const regionNames = new Intl.DisplayNames(['pt-BR'], { type: 'region' });

function countryDisplayName(iso: string): string {
  try {
    return regionNames.of(iso) ?? iso;
  } catch {
    return iso;
  }
}

const PRIMARY_ISO_BY_CALLING_CODE: Record<string, string> = {
  '1': 'US',
  '7': 'RU',
  '39': 'IT',
  '44': 'GB',
  '47': 'NO',
  '61': 'AU',
  '64': 'NZ',
  '212': 'MA',
  '262': 'RE',
  '290': 'SH',
  '358': 'FI',
  '500': 'FK',
  '590': 'GP',
  '672': 'NF',
};

function sortPhoneCountries(list: IPhoneCountry[]): IPhoneCountry[] {
  return list.sort((a, b) => {
    if (a.iso === DEFAULT_PHONE_COUNTRY) return -1;
    if (b.iso === DEFAULT_PHONE_COUNTRY) return 1;
    return a.name.localeCompare(b.name, 'pt-BR');
  });
}

function buildPhoneCountries(): IPhoneCountry[] {
  const list: IPhoneCountry[] = [];

  for (const iso of getCountries()) {
    try {
      const callingCode = getCountryCallingCode(iso);
      const name = countryDisplayName(iso);
      list.push({
        iso,
        name,
        callingCode,
        title: `${name} (+${callingCode})`,
        value: iso,
        searchText: `${name} ${iso} ${callingCode}`.toLowerCase(),
      });
    } catch {
      continue;
    }
  }

  return sortPhoneCountries(list);
}

function uniqueByCallingCode(list: IPhoneCountry[]): IPhoneCountry[] {
  const groups = new Map<string, IPhoneCountry[]>();

  for (const country of list) {
    const group = groups.get(country.callingCode) ?? [];
    group.push(country);
    groups.set(country.callingCode, group);
  }

  const unique: IPhoneCountry[] = [];

  for (const [callingCode, group] of groups) {
    const preferredIso = PRIMARY_ISO_BY_CALLING_CODE[callingCode];
    const primary =
      group.find((item) => item.iso === preferredIso) ??
      group.slice().sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))[0];

    unique.push({
      ...primary,
      searchText: group
        .map((item) => `${item.name} ${item.iso} ${item.callingCode}`)
        .join(' ')
        .toLowerCase(),
    });
  }

  return sortPhoneCountries(unique);
}

export const PHONE_COUNTRIES: IPhoneCountry[] = buildPhoneCountries();

export const PHONE_COUNTRY_SELECT_LIST: IPhoneCountry[] =
  uniqueByCallingCode(PHONE_COUNTRIES);

export const PHONE_COUNTRY_OPTIONS = PHONE_COUNTRY_SELECT_LIST.map(
  (country) => ({
    title: country.title,
    value: country.value,
  }),
);

export function filterPhoneCountries(
  countries: IPhoneCountry[],
  searchTerm: string,
): IPhoneCountry[] {
  const term = searchTerm.trim().toLowerCase().replace(/^\+/, '');
  if (!term) return countries;

  return countries.filter(
    (item) =>
      item.searchText.includes(term) ||
      item.name.toLowerCase().includes(term) ||
      item.iso.toLowerCase().includes(term) ||
      item.callingCode.includes(term) ||
      item.title.toLowerCase().includes(term),
  );
}

export function getPhoneCountry(
  iso: string | undefined | null,
): IPhoneCountry | undefined {
  const code = String(iso || DEFAULT_PHONE_COUNTRY).toUpperCase();
  return (
    PHONE_COUNTRIES.find((country) => country.iso === code) ??
    PHONE_COUNTRIES.find((country) => country.iso === DEFAULT_PHONE_COUNTRY)
  );
}

export function flagImageUrl(iso: string): string {
  return `https://flagcdn.com/w40/${iso.toLowerCase()}.png`;
}

export function callingCodeForCountry(iso: string | undefined | null): string {
  try {
    return `+${getCountryCallingCode((iso || DEFAULT_PHONE_COUNTRY) as CountryCode)}`;
  } catch {
    return '';
  }
}

export function formatNationalInput(
  country: string | undefined | null,
  raw: string | undefined | null,
): string {
  const trimmed = String(raw ?? '');
  if (!trimmed) return '';

  const digits = trimmed.replace(/\D/g, '');
  if (!digits) return '';

  if (digits.startsWith('0800')) {
    if (digits.length <= 4) return digits;
    if (digits.length <= 7) return `${digits.slice(0, 4)} ${digits.slice(4)}`;
    return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7, 11)}`;
  }

  const iso = (country || DEFAULT_PHONE_COUNTRY) as CountryCode;
  const nationalDigits = stripCountryCallingCode(iso, digits);
  try {
    return new AsYouType(iso).input(nationalDigits);
  } catch {
    return nationalDigits;
  }
}

export const E164_FLEXIBLE = /^\+[1-9]\d{7,14}$/;

export function toE164(
  country: string | undefined | null,
  national: string | undefined | null,
): string {
  const trimmed = String(national ?? '').trim();
  if (!trimmed) return '';

  const digits = trimmed.replace(/\D/g, '');
  if (!digits) return '';

  if (digits.startsWith('0800')) {
    return digits;
  }
  if (digits.startsWith('550800')) {
    return digits.slice(2);
  }

  if (trimmed.startsWith('+') && !trimmed.startsWith('+55')) {
    const parsed = parsePhoneNumberFromString(trimmed);
    if (parsed) return parsed.format('E.164');
    return `+${digits}`;
  }

  const iso = (country || DEFAULT_PHONE_COUNTRY) as CountryCode;
  const nationalDigits = stripCountryCallingCode(iso, digits);

  const parsed = parsePhoneNumberFromString(nationalDigits, iso);
  if (parsed) return parsed.format('E.164');

  if (iso === 'BR' && nationalDigits.length >= 8 && nationalDigits.length <= 11) {
    return `+55${nationalDigits}`;
  }

  try {
    const callingCode = String(getCountryCallingCode(iso));
    const alreadyHasCallingCode =
      nationalDigits.startsWith(callingCode) &&
      nationalDigits.length - callingCode.length >= 8;
    if (alreadyHasCallingCode) {
      return `+${nationalDigits}`;
    }
    return `+${callingCode}${nationalDigits}`;
  } catch {
    return nationalDigits;
  }
}

export function parseE164(
  value: string | undefined | null,
  fallbackCountry: string = DEFAULT_PHONE_COUNTRY,
): {
  country: string;
  national: string;
} {
  const raw = String(value ?? '').trim();
  const isoFallback = fallbackCountry || DEFAULT_PHONE_COUNTRY;
  if (!raw) {
    return { country: isoFallback, national: '' };
  }

  const digits = raw.replace(/\D/g, '');
  if (!digits) {
    return { country: isoFallback, national: '' };
  }
  if (digits.startsWith('0800')) {
    return { country: DEFAULT_PHONE_COUNTRY, national: digits };
  }
  if (digits.startsWith('550800')) {
    return { country: DEFAULT_PHONE_COUNTRY, national: digits.slice(2) };
  }

  if (raw.startsWith('+')) {
    const parsed = parsePhoneNumberFromString(raw);
    if (parsed?.country && parsed.isPossible()) {
      return {
        country: parsed.country,
        national: parsed.nationalNumber,
      };
    }

    const typer = new AsYouType();
    typer.input(raw);
    const typedCountry = typer.getCountry();
    const typedNational = typer.getNationalNumber() || '';
    if (typedCountry) {
      return { country: typedCountry, national: typedNational };
    }

    const callingCode = typer.getCallingCode();
    if (callingCode && digits.startsWith(callingCode)) {
      return {
        country: isoFallback,
        national: digits.slice(callingCode.length),
      };
    }
  }

  return {
    country: isoFallback,
    national: stripCountryCallingCode(isoFallback, digits),
  };
}

export function formatPhoneDisplay(
  phone: string | number | null | undefined,
): string {
  if (phone === null || phone === undefined || phone === '') return '';
  const raw = String(phone).trim();
  if (!raw) return '';

  const digits = raw.replace(/\D/g, '');
  if (digits.startsWith('0800') && digits.length >= 11) {
    return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7, 11)}`;
  }

  const e164Input = raw.startsWith('+')
    ? raw
    : (digits.length === 12 || digits.length === 13) && digits.startsWith('55')
      ? `+${digits}`
      : '';
  const parsed = e164Input ? parsePhoneNumberFromString(e164Input) : undefined;
  if (parsed) {
    if (parsed.country === 'BR') {
      const nsn = parsed.nationalNumber;
      if (nsn.length === 10) {
        return `(${nsn.slice(0, 2)}) ${nsn.slice(2, 6)}-${nsn.slice(6)}`;
      }
      if (nsn.length === 11) {
        return `(${nsn.slice(0, 2)}) ${nsn.slice(2, 7)}-${nsn.slice(7)}`;
      }
    }
    return parsed.formatInternational();
  }

  if (digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  }

  return raw.startsWith('+') ? raw : digits;
}
